import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { compact } from 'lodash'
import { LocationType } from 'app/types/entities'
import { UserType } from 'app/types/entities'
import { useUser } from 'app/utils'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { isSMCOrg } from 'lib/isSMCOrg'
import type { TabType } from 'app/components/Tabs'
import { useLocationStore } from 'app/stores/StoreContext'

export type UseLocationSettingsViewReturn = {
  title: string
  tabs: TabType[]
  canCreateLocation: boolean
}

const getTabs = (
  user: UserType,
  dniEnabled: boolean,
  commLogEnabled: boolean,
  canManageWorkspace: boolean,
): TabType[] =>
  compact([
    { name: `General`, href: `general` },
    { name: `Phone Numbers`, href: `phone-numbers` },
    canManageWorkspace ? { name: `Email`, href: `email` } : null,
    { name: `PMS Configuration`, href: `pms-config` },
    commLogEnabled ? { name: `Comm Log`, href: `comm-log` } : null,
    dniEnabled ? { name: `IVR`, href: `ivr` } : null,
    user.type === `super` || isSMCOrg(user.orgId!) ? { name: `Budget`, href: `budget` } : null,
    user.type === `super` ? { name: `Products`, href: `products`, superUserOnly: true } : null,
    isSMCOrg(user.orgId!) ? { name: `Facebook Ads`, href: `facebook-ads` } : null,
  ])

export function useLocationSettingsView(): UseLocationSettingsViewReturn {
  const user = useUser() as unknown as UserType
  const { locationId } = useParams()
  const locationStore = useLocationStore()
  const dniEnabled = useFeatureToggle(`dni`)
  const commLogEnabled = useFeatureToggle(`pms-comm-log`)
  const checkPermission = useCheckPermission()
  const canManageWorkspace = checkPermission(`settings:workspace`)

  useEffect(() => {
    if (!locationId || locationId === `new`) {
      // oxlint-disable-next-line react/immutability -- existing closure or mutable API pattern is intentional
      locationStore.current = undefined
      return
    }
    locationStore.fetchOne(locationId)
  }, [locationId])

  const isNew = locationId === `new`
  const current = locationStore.current as LocationType | undefined

  return {
    title: isNew ? `Add Location` : (current?.name ?? ``),
    tabs: getTabs(user, dniEnabled, commLogEnabled, canManageWorkspace),
    canCreateLocation: !isNew,
  }
}
